// @ts-nocheck
import React from 'react';
import { ModDefinition, ModIcon } from '../utils/ModRegistry';
import { useAuth } from '../contexts/AuthContext';
import { stickerTextStyle } from './StickerText';

interface ModPreviewModalProps {
    isOpen: boolean;
    onClose: () => void;
    mod: ModDefinition | null;
    onGetMod: (modId: string) => void;
    onOpenMod: (modId: string) => void;
    isEquipped: boolean;
}

export const ModPreviewModal: React.FC<ModPreviewModalProps> = ({
    isOpen,
    onClose,
    mod,
    onGetMod,
    onOpenMod,
    isEquipped
}) => {
    const { currentUser, usersData } = useAuth();
    if (!isOpen || !mod) return null;

    const currentUserData = usersData[currentUser];
    const userThemeColor = currentUserData?.appThemeColor || '#3b82f6';
    const userTextColor = currentUserData?.appTextColor || '#ffffff';
    const userOpacity = (currentUserData?.postBlurOpacity !== undefined ? currentUserData.postBlurOpacity : 50) / 100;

    const getExactModalBg = (color: string, opacityVal: number) => {
        if (!color) return `rgba(26, 26, 46, ${opacityVal})`;
        if (color.startsWith('rgba')) return color.replace(/[\d.]+\)$/, `${opacityVal})`);
        if (color.startsWith('rgb')) return color.replace('rgb', 'rgba').replace(')', `, ${opacityVal})`);
        let hex = color.replace('#', '');
        if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        try {
            const r = parseInt(hex.substring(0, 2), 16);
            const g = parseInt(hex.substring(2, 4), 16);
            const b = parseInt(hex.substring(4, 6), 16);
            return `rgba(${r}, ${g}, ${b}, ${opacityVal})`;
        } catch {
            return `rgba(26, 26, 46, ${opacityVal})`;
        }
    };

    const modalBg = getExactModalBg(mod.themeColor || userThemeColor, userOpacity);

    return (
        <div 
            className="fixed inset-0 flex flex-col items-center justify-center z-[100] p-4 animate-fade-in no-swipe touch-none overscroll-none"
            onClick={onClose}
        >
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm -z-10" />
            
            <div className="group relative p-1 rounded-[2.5rem] transition-all hover:scale-[1.02] w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/30 to-purple-600/30 rounded-[2.5rem] opacity-0 group-hover:opacity-100 transition-opacity blur-lg" />
                
                <div 
                    className="relative bg-[#161b22]/80 backdrop-blur-xl border border-white/10 rounded-[2.4rem] p-6 flex flex-col shadow-xl"
                    style={{ backgroundColor: modalBg, color: userTextColor }}
                >
                    <div className="flex items-start justify-between mb-4">
                        <div className="w-16 h-16 rounded-full bg-black/20 overflow-hidden shadow-inner flex items-center justify-center">
                            <ModIcon icon={mod.icon} className="w-full h-full object-cover" />
                        </div>
                        <div className="text-[10px] font-black text-blue-400/60 uppercase tracking-widest">Modular</div>
                    </div>
                    
                    <h3 className="text-xl font-black italic uppercase tracking-tighter mb-2 text-white">{mod.name}</h3>
                    {mod.author && (
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">
                            By @{mod.author}
                        </p>
                    )}
                    <p className="text-[11px] font-medium text-gray-400 mb-6 leading-relaxed">
                        {mod.description || 'A unique custom mod for GoodLyfe.'}
                    </p>
                    
                    <div className="mt-auto space-y-2">
                        <button
                            onClick={() => {
                                if (isEquipped) {
                                    onOpenMod(mod.id);
                                } else {
                                    onGetMod(mod.id);
                                }
                                onClose();
                            }}
                            className={`w-full py-4 rounded-2xl font-black uppercase tracking-widest text-xs italic border-2 transition-all ${
                                isEquipped 
                                ? 'bg-blue-600 border-white text-white shadow-lg shadow-blue-500/20' 
                                : 'bg-white/10 border-transparent text-white/70 hover:bg-white/20'
                            }`}
                        >
                            {isEquipped ? 'Open Mod' : 'Equip Mod'}
                        </button>
                        
                        <button
                            onClick={onClose}
                            className="w-full py-4 rounded-2xl font-black uppercase tracking-widest text-xs italic border-2 border-transparent bg-black/20 text-white/50 hover:bg-black/40 transition-all"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
